import React from "react";
import LegalPageNav from "@/components/LegalPageNav";
import NewTabHint from "@/components/NewTabHint";
import PageLayout from "@/components/PageLayout";
import { Link } from "react-router-dom";

export default function AccessibilityStatement() {
  return (
    <PageLayout>
      <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">Accessibility Statement</h1>
      <p className="font-body text-sm text-muted-foreground mb-10">Last updated: July 30, 2026</p>

      <div className="space-y-8 font-body text-foreground/90">

        <section aria-labelledby="a11y-commitment">
          <h2 id="a11y-commitment" className="font-display text-2xl text-foreground mb-3">Conformance Status</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong>donnamargheritanyc.com</strong> is designed and built to conform with the{" "}
            <strong>Web Content Accessibility Guidelines (WCAG) 2.2 at Level AA</strong>: the current W3C standard.
            WCAG 2.2 AA conformance also satisfies all WCAG 2.0 and 2.1 AA requirements. The site follows the
            four core principles: <strong>Perceivable, Operable, Understandable, and Robust (POUR)</strong>.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed mt-3">
            This statement covers the website at <a href="https://donnamargheritanyc.com" className="text-primary hover:underline">donnamargheritanyc.com</a> and
            was last audited in July 2026 using a combination of automated tools and manual keyboard-only testing.
            We also aim to meet the requirements of the <strong>Americans with Disabilities Act (ADA) Title III</strong> and{" "}
            <strong>Section 508 of the Rehabilitation Act</strong>, both of which reference WCAG Level AA as the technical benchmark.
          </p>
        </section>

        <section aria-labelledby="a11y-measures">
          <h2 id="a11y-measures" className="font-display text-2xl text-foreground mb-3">Technical Measures in Place</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            The following technical measures have been implemented across every page of this website:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground leading-relaxed">
            <li>
              <strong>Semantic HTML5 landmarks</strong>: every page uses <code>&lt;header&gt;</code>, <code>&lt;nav&gt;</code>,{" "}
              <code>&lt;main&gt;</code>, and <code>&lt;footer&gt;</code> elements with a single, logical heading hierarchy
              (one <code>&lt;h1&gt;</code> per page, followed by <code>&lt;h2&gt;</code> and <code>&lt;h3&gt;</code> in order).
            </li>
            <li>
              <strong>Skip to main content</strong>: a visually hidden skip link becomes visible on first Tab press and
              routes focus directly to the <code>&lt;main&gt;</code> element, bypassing the navigation on every page
              (WCAG §2.4.1 Bypass Blocks).
            </li>
            <li>
              <strong>Keyboard-only operation</strong>: every link, button, form field, menu item, and toggle is
              reachable by <kbd>Tab</kbd> / <kbd>Shift+Tab</kbd> and operable by <kbd>Enter</kbd>, <kbd>Space</kbd>,
              and <kbd>Escape</kbd> where applicable. No keyboard traps exist outside intentional modal dialogs, which
              implement a full focus-trap with Escape-to-close.
            </li>
            <li>
              <strong>Visible focus indicators</strong>: a high-contrast 2 px <code>outline</code> ring (not
              box-shadow, which can be clipped) is rendered on every focused interactive element via{" "}
              <code>:focus-visible</code>, ensuring it works for keyboard users without affecting mouse users
              (WCAG 2.2 §2.4.11 Focus Appearance; §2.4.12 Focus Not Obscured).
            </li>
            <li>
              <strong>Focus not obscured by sticky header</strong>: all <code>&lt;main&gt;</code> elements carry{" "}
              <code>scroll-margin-top: 80px</code> and <code>tabindex="-1"</code> so the skip link never places focus
              under the fixed navigation bar (WCAG 2.2 §2.4.12).
            </li>
            <li>
              <strong>Colour contrast</strong>: body text meets the 4.5:1 ratio; large text (≥18 pt or ≥14 pt bold)
              and UI components (borders, icons) meet 3:1 (WCAG §1.4.3 Contrast (Minimum); §1.4.11 Non-text Contrast).
            </li>
            <li>
              <strong>Alternative text</strong>: every meaningful image has a descriptive <code>alt</code> attribute.
              Decorative images and icon-only SVGs use <code>alt=""</code> or <code>aria-hidden="true"</code> so screen
              readers skip them (WCAG §1.1.1 Non-text Content).
            </li>
            <li>
              <strong>Labelled form fields</strong>: every <code>&lt;input&gt;</code> and{" "}
              <code>&lt;textarea&gt;</code> is associated with a visible <code>&lt;label&gt;</code> via{" "}
              <code>for</code>/<code>id</code>. Required fields are marked with a visible asterisk and a screen-reader
              label. Error messages are rendered as plain text adjacent to the relevant field (WCAG §1.3.1; §3.3.1; §3.3.2).
            </li>
            <li>
              <strong>Reduced motion</strong>: a <code>@media (prefers-reduced-motion: reduce)</code> rule in the
              global stylesheet disables all CSS transitions and animations for users who have requested reduced motion
              in their operating system. The accessibility widget's "Pause Animations" toggle provides the same control
              on demand (WCAG §2.3.3 Animation from Interactions).
            </li>
            <li>
              <strong>Touch and click target size</strong>: all interactive elements are at least 44 × 44 CSS pixels,
              including the gear widget button, all navigation links, and all form controls. This exceeds the WCAG 2.2
              minimum of 24 × 24 px (§2.5.8 Target Size (Minimum)).
            </li>
            <li>
              <strong>No drag requirement</strong>: no functionality requires a dragging motion. Widget repositioning
              is accomplished with a single click/tap (WCAG 2.2 §2.5.7 Dragging Movements).
            </li>
            <li>
              <strong>Consistent help and contact information</strong>: the restaurant's phone number, address, and a
              link to the Contact page appear in the same footer location on every page (WCAG 2.2 §3.2.6 Consistent Help).
            </li>
            <li>
              <strong>No redundant entry</strong>: the contact form collects each piece of information once; users are
              never asked to re-enter data already provided in the same session (WCAG 2.2 §3.3.7 Redundant Entry).
            </li>
            <li>
              <strong>No cognitive authentication barriers</strong>: the site does not require CAPTCHAs, puzzles, or
              memory-based tests. The login flow (used by staff only) supports copy-paste and password managers
              (WCAG 2.2 §3.3.8 Accessible Authentication (Minimum)).
            </li>
            <li>
              <strong>ARIA used judiciously</strong>: ARIA roles (<code>dialog</code>, <code>switch</code>,{" "}
              <code>navigation</code>, <code>group</code>) are applied only where native HTML cannot convey the required
              semantics. No ARIA attribute overrides a native element's semantics (WCAG §4.1.2 Name, Role, Value).
            </li>
            <li>
              <strong>Focus returned to the trigger</strong>: when a dialog (accessibility panel, cookie settings, mobile
              navigation) is closed by Escape, its close button, or a click outside, keyboard focus returns to the control
              that opened it, so users never lose their place (WCAG §2.4.3 Focus Order).
            </li>
            <li>
              <strong>Input purpose identified</strong>: contact form fields declare <code>autocomplete</code> values
              (<code>name</code>, <code>email</code>, <code>tel</code>) so browsers, password managers, and assistive
              technology can fill them automatically (WCAG §1.3.5 Identify Input Purpose).
            </li>
            <li>
              <strong>Errors announced</strong>: form validation messages are rendered in an{" "}
              <code>aria-live="assertive"</code> alert region and receive focus, so screen reader users hear the problem
              immediately (WCAG §3.3.1 Error Identification; §4.1.3 Status Messages).
            </li>
            <li>
              <strong>New-tab links announced</strong>: links that open in a new tab include screen-reader-only text
              stating so, and carry <code>rel="noopener"</code> (WCAG §3.2.5 Change on Request).
            </li>
            <li>
              <strong>Live regions</strong>: dynamic value changes in the accessibility widget (e.g. step counter
              values) are announced to screen readers via <code>aria-live="polite"</code> (WCAG §4.1.3 Status Messages).
            </li>
            <li>
              <strong>Language declared</strong>: the page language is set to <code>lang="en-US"</code> on the{" "}
              <code>&lt;html&gt;</code> element (WCAG §3.1.1 Language of Page).
            </li>
            <li>
              <strong>No flashing content</strong>: the site contains no content that flashes more than three times per
              second (WCAG §2.3.1 Three Flashes or Below Threshold).
            </li>
          </ul>
        </section>

        <section aria-labelledby="a11y-widget">
          <h2 id="a11y-widget" className="font-display text-2xl text-foreground mb-3">Built-in Accessibility Widget</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            Click the gear icon (bottom corner of every page), then select <strong>Accessibility</strong>. The panel
            is itself fully keyboard accessible: Tab to reach it, Enter/Space to activate controls, Escape to close.
            A complete focus trap prevents keyboard users from leaving the dialog unintentionally. All settings
            persist in <code>localStorage</code> across pages and visits.
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
            <li><strong>Oversized Widget</strong>: enlarges all widget controls for easier interaction.</li>
            <li><strong>Contrast +</strong>: cycles: Normal → Increased → High → Inverted.</li>
            <li><strong>Highlight Links</strong>: adds a visible outline and underline to every link.</li>
            <li><strong>Bigger Text</strong>: increases base font size in three steps.</li>
            <li><strong>Text Spacing</strong>: increases letter and word spacing in two steps.</li>
            <li><strong>Pause Animations</strong>: stops all CSS and JS-driven animations.</li>
            <li><strong>Hide Images</strong>: hides all images for distraction-free reading.</li>
            <li><strong>Dyslexia Friendly</strong>: switches body text to a dyslexia-friendly typeface.</li>
            <li><strong>Big Cursor</strong>: enlarges the system cursor.</li>
            <li><strong>Tooltips</strong>: surfaces <code>title</code> attribute text as visible tooltips on hover/focus.</li>
            <li><strong>Line Height</strong>: increases line spacing in two steps.</li>
            <li><strong>Text Align</strong>: left, centre, right, or justified.</li>
            <li><strong>Saturation</strong>: low, high, grayscale, or monochrome.</li>
            <li><strong>Reset All Settings</strong>: clears every preference with a single click.</li>
            <li><strong>Move Widget</strong>: repositions the gear to the left or right side (single click, no dragging).</li>
            <li><strong>Hide Widget</strong>: hides the gear icon. Press <kbd>Alt</kbd>+<kbd>0</kbd> to restore it.</li>
          </ul>
        </section>

        <section aria-labelledby="a11y-limitations">
          <h2 id="a11y-limitations" className="font-display text-2xl text-foreground mb-3">Known Limitations</h2>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">

            <li>
              The OpenTable reservation booking flow is provided by a third party. We recommend contacting OpenTable
              directly or calling us at <a href="tel:+12127721169" className="text-primary hover:underline">(212) 772-1169</a> if
              you experience accessibility barriers during the reservation process.
            </li>
          </ul>
        </section>

        <section aria-labelledby="a11y-feedback">
          <h2 id="a11y-feedback" className="font-display text-2xl text-foreground mb-3">Feedback and Complaints</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            We welcome feedback. If you encounter a barrier, find content that is not accessible, or need information
            in an alternative format, please contact us through any of the following channels:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
            <li>
              Email:{" "}
              <a href="mailto:info@donnamargheritanyc.com" className="text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 rounded-sm">
                info@donnamargheritanyc.com
              </a>
            </li>
            <li>
              Phone:{" "}
              <a href="tel:+12127721169" className="text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 rounded-sm">
                (212) 772-1169
              </a>
            </li>
            <li>Address: Donna Margherita, 1304A Second Avenue, New York, NY 10065</li>
          </ul>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            We aim to respond within <strong>five business days</strong> and will provide the information or service
            requested through an accessible alternative where possible.
          </p>
        </section>

        <section aria-labelledby="a11y-enforcement">
          <h2 id="a11y-enforcement" className="font-display text-2xl text-foreground mb-3">Enforcement Procedure (US)</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This website is operated from the United States. If you are not satisfied with our response, you may contact
            the <strong>US Department of Justice ADA Information Line</strong> at{" "}
            <a href="tel:+18005140301" className="text-primary hover:underline">1-800-514-0301</a> (voice) or{" "}
            <a href="tel:+18005141616" className="text-primary hover:underline">1-800-514-1616</a> (TTY). Additional
            guidance is available at{" "}
            <a href="https://www.ada.gov" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
              ada.gov<NewTabHint />
            </a>.
          </p>
        </section>

        <LegalPageNav exclude={["/accessibility-statement"]} />

      </div>
    </PageLayout>
  );
}